/**
 * Derivação PURA do passo inicial da cerimônia (#96, D-063 item 1 — retomada de
 * estado): no mount, a UI lê os carimbos que o servidor apura como FATO
 * (`SignerView.channel_verified_at` / `consent_given_at`, ver `packages/shared/
 * src/dtos.ts`) e retoma do ponto certo — nunca refaz OTP verificado, nunca
 * finge que um consentimento registrado sumiu.
 *
 * Módulo sem DOM/rede/hooks, no espírito de `ceremonyScreen.ts` — testado em
 * `ceremonySteps.test.ts` (gate 3 da rodada, #87).
 */

/** Passos da modalidade Simples (D-063: identificação → canal/OTP → consentimento → assinatura). */
export type SimpleStep = 'identification' | 'channel' | 'consent' | 'signature';

/**
 * Passos da modalidade Qualificada (D-063 item 3: identificação → consentimento →
 * certificado → assinatura; a "assinatura" vive DENTRO do passo de certificado —
 * escolher a chave e assinar são a mesma interação com o agente local).
 */
export type QualifiedStep = 'identification' | 'consent' | 'certificate';

export interface ResumeInput {
  channel_verified_at: string | null;
  consent_given_at: string | null;
}

/**
 * Simples: canal não verificado → começa da identificação (o OTP vem logo em
 * seguida). Canal verificado → pula direto ao consentimento; consentimento já
 * registrado → assinatura direta. A indicação visível de "você já consentiu em
 * DD/MM HH:mm" é responsabilidade da tela (SignPage), usando `consent_given_at`.
 */
export function initialSimpleStep(signer: ResumeInput): SimpleStep {
  if (!signer.channel_verified_at) return 'identification';
  if (!signer.consent_given_at) return 'consent';
  return 'signature';
}

/**
 * Qualificada: não há canal a verificar (a autenticação é a posse da chave,
 * D-035) — o que retoma é o consentimento: já registrado → direto à escolha do
 * certificado; senão → identificação seguida do consentimento sobre o documento
 * em tela (ordem mandada pela D-063 item 3: consentir ANTES de escolher a chave).
 */
export function initialQualifiedStep(signer: ResumeInput): QualifiedStep {
  if (signer.consent_given_at) return 'certificate';
  return 'identification';
}
